import { useState, useEffect } from "react";
import { useAuth } from "./AuthContext";

const API_BASE = "http://localhost:8000";

// ─── Mock data for when API returns nothing ───────────────────────────────────

const MOCK_CUSTOMERS = [
  {
    customer_id: "cust_demo_001",
    name: "Acme Community Bank",
    industry: "Community Bank",
    company_size: "51–200",
    status: "active",
    regulatory_obligations: { frameworks: ["glba","ffiec"], regulator: "FDIC", contact_name: "Jane Smith", contact_email: "jane@acmebank.com", contact_title: "CISO", onboarded_at: "2026-02-01T10:00:00" },
    created_at: "2026-02-01T10:00:00",
    overall_score: 2.4,
    completed_categories: 8,
  },
  {
    customer_id: "cust_demo_002",
    name: "Meridian Credit Union",
    industry: "Credit Union",
    company_size: "201–500",
    status: "active",
    regulatory_obligations: { frameworks: ["glba","nist"], regulator: "NCUA", contact_name: "Bob Torres", contact_email: "btorres@meridian.org", contact_title: "IT Director", onboarded_at: "2026-02-10T14:00:00" },
    created_at: "2026-02-10T14:00:00",
    overall_score: 3.1,
    completed_categories: 12,
  },
  {
    customer_id: "cust_demo_003",
    name: "Pinnacle Payments Inc.",
    industry: "Payment Processor",
    company_size: "51–200",
    status: "active",
    regulatory_obligations: { frameworks: ["glba","pcidss","sox"], regulator: "State Regulator", contact_name: "Alice Chen", contact_email: "achen@pinnaclepay.com", contact_title: "VP Compliance", onboarded_at: "2026-02-15T09:00:00" },
    created_at: "2026-02-15T09:00:00",
    overall_score: 1.8,
    completed_categories: 5,
  },
];

// ─── Config ───────────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  pending:       { label: "Pending",        color: "#94a3b8", bg: "#94a3b815" },
  invoice_sent:  { label: "Invoice Sent",   color: "#f59e0b", bg: "#f59e0b15" },
  active:        { label: "Active",         color: "#22c55e", bg: "#22c55e15" },
  inactive:      { label: "Inactive",       color: "#64748b", bg: "#64748b15" },
  suspended:     { label: "Suspended",      color: "#ef4444", bg: "#ef444415" },
};

const FW_COLORS = {
  glba: "#3b82f6", pcidss: "#f59e0b", sox: "#ef4444",
  nist: "#22c55e", ffiec: "#a78bfa",
};
const FW_LABELS = {
  glba: "GLBA", pcidss: "PCI-DSS", sox: "SOX", nist: "NIST", ffiec: "FFIEC",
};

function scoreColor(score) {
  if (score === null || score === undefined) return "#334155";
  if (score >= 3.5) return "#22c55e";
  if (score >= 2.5) return "#f59e0b";
  return "#ef4444";
}

// ─── KPI strip ────────────────────────────────────────────────────────────────

function KPIStrip({ customers }) {
  const active    = customers.filter(c => c.status === "active").length;
  const avgScore  = customers.filter(c => c.overall_score).length
    ? (customers.reduce((s, c) => s + (c.overall_score || 0), 0) / customers.filter(c => c.overall_score).length).toFixed(1)
    : "—";
  const frameworks = [...new Set(customers.flatMap(c => c.regulatory_obligations?.frameworks || []))].length;

  return (
    <div style={sty.kpiStrip}>
      {[
        { label: "Total Customers",    value: customers.length, color: "#f1f5f9" },
        { label: "Active",             value: active,           color: "#22c55e" },
        { label: "Avg Maturity Score", value: avgScore ? `${avgScore}/5` : "—", color: "#f59e0b" },
        { label: "Frameworks in Use",  value: frameworks,       color: "#a78bfa" },
      ].map(k => (
        <div key={k.label} style={sty.kpi}>
          <div style={{ ...sty.kpiVal, color: k.color }}>{k.value}</div>
          <div style={sty.kpiLabel}>{k.label}</div>
        </div>
      ))}
    </div>
  );
}

// ─── Customer Row ─────────────────────────────────────────────────────────────

function CustomerRow({ customer, selected, onClick }) {
  const sc  = STATUS_CONFIG[customer.status] || STATUS_CONFIG.active;
  const reg = customer.regulatory_obligations || {};
  const fws = reg.frameworks || [];
  const onboarded = customer.created_at
    ? new Date(customer.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "—";

  return (
    <tr
      style={{ ...sty.tr, ...(selected ? sty.trSelected : {}), cursor: "pointer" }}
      onClick={onClick}
    >
      <td style={sty.td}>
        <div style={sty.custName}>{customer.name}</div>
        <div style={sty.custIndustry}>{customer.industry || "—"}</div>
      </td>
      <td style={sty.td}>
        <span style={{ ...sty.statusPill, background: sc.bg, color: sc.color }}>
          {sc.label}
        </span>
      </td>
      <td style={sty.td}>
        <div style={sty.fwTags}>
          {fws.map(fw => (
            <span key={fw} style={{ ...sty.fwTag, color: FW_COLORS[fw] || "#64748b", background: (FW_COLORS[fw] || "#64748b") + "15" }}>
              {FW_LABELS[fw] || fw.toUpperCase()}
            </span>
          ))}
          {fws.length === 0 && <span style={{ color: "#334155", fontSize: 11 }}>—</span>}
        </div>
      </td>
      <td style={{ ...sty.td, textAlign: "center" }}>
        {customer.overall_score != null
          ? <span style={{ ...sty.scoreVal, color: scoreColor(customer.overall_score) }}>
              {Number(customer.overall_score).toFixed(1)}<span style={{ color: "#334155", fontSize: 11 }}>/5</span>
            </span>
          : <span style={{ color: "#334155", fontSize: 12 }}>Not assessed</span>
        }
      </td>
      <td style={{ ...sty.td, color: "#475569", fontSize: 12 }}>
        {customer.company_size || "—"}
      </td>
      <td style={{ ...sty.td, color: "#334155", fontSize: 12, fontFamily: "'DM Mono', monospace" }}>
        {onboarded}
      </td>
    </tr>
  );
}

// ─── Customer Detail Panel ────────────────────────────────────────────────────

function DetailPanel({ customer, onClose, onStatusChange, onInvoiceSent, onActivated, authFetch }) {
  const reg  = customer.regulatory_obligations || {};
  const fws  = reg.frameworks || [];
  const sc   = STATUS_CONFIG[customer.status] || STATUS_CONFIG.pending;
  const [changingStatus, setChangingStatus] = useState(false);
  const [invoicing, setInvoicing]           = useState(false);
  const [activating, setActivating]         = useState(false);
  const [billingMsg, setBillingMsg]         = useState("");
  const [invoiceStatus, setInvoiceStatus]   = useState(null);
  const [checkingInvoice, setCheckingInvoice] = useState(false);

  const handleStatusChange = async (newStatus) => {
    setChangingStatus(true);
    try {
      await authFetch(`/api/customers/${customer.customer_id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      }).catch(() => {});
      onStatusChange(customer.customer_id, newStatus);
    } finally {
      setChangingStatus(false);
    }
  };

  const handleGenerateInvoice = async () => {
    setInvoicing(true);
    setBillingMsg("");
    try {
      const res = await authFetch("/api/billing/generate-invoice", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customer_id: customer.customer_id, payment_term_days: 30 }),
      });
      const data = await res.json();
      if (!res.ok) {
        setBillingMsg(`Error: ${data.detail || "Failed to generate invoice"}`);
        return;
      }
      setBillingMsg(`✓ Invoice sent to ${customer.regulatory_obligations?.contact_email || "customer"}. Due: ${data.due_date}`);
      onInvoiceSent(customer.customer_id, data);
    } catch (e) {
      setBillingMsg("Network error — could not reach billing API");
    } finally {
      setInvoicing(false);
    }
  };

  const handleActivate = async () => {
    setActivating(true);
    setBillingMsg("");
    try {
      const res = await authFetch(`/api/billing/activate/${customer.customer_id}`, {
        method: "POST",
      });
      const data = await res.json();
      if (!res.ok) {
        setBillingMsg(`Error: ${data.detail || "Activation failed"}`);
        return;
      }
      setBillingMsg(`✓ ${customer.name} is now active`);
      onActivated(customer.customer_id);
    } catch (e) {
      setBillingMsg("Network error — could not reach billing API");
    } finally {
      setActivating(false);
    }
  };

  const handleCheckInvoice = async () => {
    setCheckingInvoice(true);
    try {
      const res = await authFetch(`/api/billing/invoice-status/${customer.customer_id}`);
      const data = await res.json();
      setInvoiceStatus(data.status || "Unknown");
    } catch {
      setInvoiceStatus("Error checking status");
    } finally {
      setCheckingInvoice(false);
    }
  };

  return (
    <div style={sty.detailPanel}>
      {/* Panel header */}
      <div style={sty.detailHeader}>
        <div>
          <div style={sty.detailName}>{customer.name}</div>
          <div style={sty.detailIndustry}>{customer.industry} · {customer.company_size || "Unknown size"}</div>
        </div>
        <button style={sty.closeBtn} onClick={onClose}>✕</button>
      </div>

      {/* Status */}
      <div style={sty.detailSection}>
        <div style={sty.detailSectionTitle}>Status</div>
        <div style={sty.statusRow}>
          <span style={{ ...sty.statusPill, background: sc.bg, color: sc.color }}>{sc.label}</span>
          <div style={sty.statusActions}>
            {Object.entries(STATUS_CONFIG)
              .filter(([k]) => k !== customer.status)
              .map(([k, v]) => (
                <button key={k}
                  style={{ ...sty.statusChangeBtn, color: v.color, borderColor: v.color + "40", opacity: changingStatus ? 0.5 : 1 }}
                  onClick={() => handleStatusChange(k)}
                  disabled={changingStatus}
                >
                  → {v.label}
                </button>
              ))}
          </div>
        </div>
      </div>

      {/* Contact */}
      <div style={sty.detailSection}>
        <div style={sty.detailSectionTitle}>Primary Contact</div>
        <div style={sty.detailRows}>
          <DetailRow label="Name"  value={reg.contact_name  || "—"} />
          <DetailRow label="Email" value={reg.contact_email || "—"} />
          <DetailRow label="Title" value={reg.contact_title || "—"} />
        </div>
      </div>

      {/* Regulatory */}
      <div style={sty.detailSection}>
        <div style={sty.detailSectionTitle}>Regulatory Profile</div>
        <div style={sty.detailRows}>
          <DetailRow label="Regulator" value={reg.regulator || "—"} />
          <DetailRow label="Revenue"   value={reg.revenue_range || "—"} />
        </div>
        <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 5 }}>
          {fws.map(fw => (
            <span key={fw} style={{ ...sty.fwTag, color: FW_COLORS[fw] || "#64748b", background: (FW_COLORS[fw] || "#64748b") + "15" }}>
              {FW_LABELS[fw] || fw.toUpperCase()}
            </span>
          ))}
        </div>
      </div>

      {/* Assessment */}
      <div style={sty.detailSection}>
        <div style={sty.detailSectionTitle}>Assessment</div>
        <div style={sty.detailRows}>
          <DetailRow label="Overall Score"
            value={customer.overall_score != null
              ? <span style={{ color: scoreColor(customer.overall_score) }}>{Number(customer.overall_score).toFixed(1)}/5</span>
              : "Not assessed"} />
          <DetailRow label="Categories Rated" value={`${customer.completed_categories || 0}/15`} />
        </div>
      </div>

      {/* System */}
      <div style={sty.detailSection}>
        <div style={sty.detailSectionTitle}>System</div>
        <div style={sty.detailRows}>
          <DetailRow label="Customer ID" value={<span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#475569" }}>{customer.customer_id}</span>} />
          <DetailRow label="Onboarded"   value={customer.created_at ? new Date(customer.created_at).toLocaleString() : "—"} />
          <DetailRow label="Collection"  value={<span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#475569" }}>{customer.qdrant_collection_name || "—"}</span>} />
        </div>
      </div>

      {/* Billing */}
      <div style={sty.detailSection}>
        <div style={sty.detailSectionTitle}>Billing</div>
        <div style={sty.detailRows}>
          <DetailRow label="Tier"         value={customer.tier || "—"} />
          <DetailRow label="Invoice"      value={customer.square_invoice_id
            ? <a href={customer.square_invoice_url} target="_blank" rel="noreferrer"
                style={{ color: "#60a5fa", fontSize: 11 }}>View Invoice ↗</a>
            : "Not generated"} />
          <DetailRow label="Due Date"     value={customer.invoice_due_date || "—"} />
          <DetailRow label="Amount"       value={customer.invoice_amount
            ? `$${Number(customer.invoice_amount).toLocaleString()}`
            : "—"} />
        </div>

        {/* Live Square status check */}
        {customer.square_invoice_id && (
          <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8 }}>
            <button
              style={{ ...sty.statusChangeBtn, color: "#60a5fa", borderColor: "#60a5fa40" }}
              onClick={handleCheckInvoice}
              disabled={checkingInvoice}
            >
              {checkingInvoice ? "Checking..." : "Check Square Status"}
            </button>
            {invoiceStatus && (
              <span style={{ fontSize: 11, color: "#94a3b8", fontFamily: "'DM Mono', monospace" }}>
                {invoiceStatus}
              </span>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          {/* Generate Invoice — only if not yet sent */}
          {!customer.square_invoice_id && (
            <button
              style={{ ...sty.billingBtn, background: "#1e3a5f", borderColor: "#1d4ed860", color: "#93c5fd" }}
              onClick={handleGenerateInvoice}
              disabled={invoicing}
            >
              {invoicing ? "⟳ Sending..." : "✉ Generate & Send Invoice"}
            </button>
          )}

          {/* Activate — only if invoice exists and not yet active */}
          {customer.square_invoice_id && customer.status !== "active" && (
            <button
              style={{ ...sty.billingBtn, background: "#14532d", borderColor: "#22c55e60", color: "#86efac" }}
              onClick={handleActivate}
              disabled={activating}
            >
              {activating ? "⟳ Activating..." : "✓ Activate Account"}
            </button>
          )}

          {/* Already active indicator */}
          {customer.status === "active" && (
            <div style={{ fontSize: 12, color: "#22c55e", fontFamily: "'DM Mono', monospace" }}>
              ✓ Account is active
            </div>
          )}
        </div>

        {/* Feedback message */}
        {billingMsg && (
          <div style={{
            marginTop: 10, fontSize: 12, padding: "8px 10px", borderRadius: 6,
            background: billingMsg.startsWith("✓") ? "#14532d" : "#450a0a",
            color: billingMsg.startsWith("✓") ? "#86efac" : "#fca5a5",
            fontFamily: "'DM Mono', monospace",
          }}>
            {billingMsg}
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={sty.detailActions}>
        <a href={`/?customer_id=${customer.customer_id}`} style={sty.actionBtn}>
          View Dashboard →
        </a>
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div style={sty.detailRow}>
      <span style={sty.detailRowLabel}>{label}</span>
      <span style={sty.detailRowValue}>{value}</span>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function AdminCustomers() {
  const { authFetch } = useAuth();
  const [customers, setCustomers]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [selected, setSelected]     = useState(null);
  const [search, setSearch]         = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => { loadCustomers(); }, []); // eslint-disable-line

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`/api/customers/`);
      if (res.ok) {
        const data = await res.json();
        const enriched = await Promise.all(
          (data.customers || []).map(async c => {
            try {
              const aRes = await authFetch(`/api/assessments/current?customer_id=${c.customer_id}`);
              if (aRes.ok) {
                const aData = await aRes.json();
                return {
                  ...c,
                  overall_score: aData.assessment?.overall_score ?? null,
                  completed_categories: aData.assessment?.completed_categories ?? 0,
                };
              }
            } catch (_) {}
            return c;
          })
        );
        setCustomers(enriched.length > 0 ? enriched : MOCK_CUSTOMERS);
      } else {
        setCustomers(MOCK_CUSTOMERS);
      }
    } catch (_) {
      setCustomers(MOCK_CUSTOMERS);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (customerId, newStatus) => {
    setCustomers(prev => prev.map(c =>
      c.customer_id === customerId ? { ...c, status: newStatus } : c
    ));
    if (selected?.customer_id === customerId) {
      setSelected(prev => ({ ...prev, status: newStatus }));
    }
  };

  const handleInvoiceSent = (customerId, invoiceData) => {
    setCustomers(prev => prev.map(c =>
      c.customer_id === customerId
        ? { ...c, status: "invoice_sent", square_invoice_id: invoiceData.square_invoice_id, square_invoice_url: invoiceData.invoice_url, invoice_due_date: invoiceData.due_date, invoice_amount: invoiceData.amount }
        : c
    ));
    if (selected?.customer_id === customerId) {
      setSelected(prev => ({ ...prev, status: "invoice_sent", square_invoice_id: invoiceData.square_invoice_id, square_invoice_url: invoiceData.invoice_url, invoice_due_date: invoiceData.due_date, invoice_amount: invoiceData.amount }));
    }
  };

  const handleActivated = (customerId) => {
    setCustomers(prev => prev.map(c =>
      c.customer_id === customerId ? { ...c, status: "active" } : c
    ));
    if (selected?.customer_id === customerId) {
      setSelected(prev => ({ ...prev, status: "active" }));
    }
  };

  const filtered = customers.filter(c => {
    const matchSearch = !search ||
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      (c.industry || "").toLowerCase().includes(search.toLowerCase()) ||
      (c.regulatory_obligations?.contact_name || "").toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === "all" || c.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div style={sty.page}>
      {/* Header */}
      <div style={sty.header}>
        <div>
          <div style={sty.breadcrumb}>Admin / Customers</div>
          <h1 style={sty.title}>Customer Management</h1>
          <p style={sty.subtitle}>Manage all CISO Advisor customer accounts</p>
        </div>
        <a href="/onboarding" style={sty.addBtn}>+ Onboard New Customer</a>
      </div>

      {/* KPI strip */}
      {!loading && <KPIStrip customers={customers} />}

      {/* Toolbar */}
      <div style={sty.toolbar}>
        <input style={sty.searchInput}
          placeholder="Search customers, contacts..."
          value={search}
          onChange={e => setSearch(e.target.value)} />
        <div style={sty.filterGroup}>
          {["all", "pending", "invoice_sent", "active", "inactive", "suspended"].map(s => (
            <button key={s}
              style={{ ...sty.filterBtn, ...(statusFilter === s ? sty.filterBtnActive : {}) }}
              onClick={() => setStatusFilter(s)}>
              {s === "all" ? "All" : STATUS_CONFIG[s]?.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main layout */}
      <div style={{ ...sty.mainLayout, gridTemplateColumns: selected ? "1fr 360px" : "1fr" }}>
        {/* Table */}
        <div style={sty.tableWrap}>
          {loading ? (
            <div style={sty.loadingState}>Loading customers...</div>
          ) : filtered.length === 0 ? (
            <div style={sty.emptyState}>
              {search || statusFilter !== "all"
                ? "No customers match your filters."
                : "No customers yet. Onboard your first customer to get started."}
            </div>
          ) : (
            <table style={sty.table}>
              <thead>
                <tr>
                  {["Customer", "Status", "Frameworks", "Maturity", "Size", "Onboarded"].map(h => (
                    <th key={h} style={sty.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => (
                  <CustomerRow
                    key={c.customer_id}
                    customer={c}
                    selected={selected?.customer_id === c.customer_id}
                    onClick={() => setSelected(selected?.customer_id === c.customer_id ? null : c)}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Detail panel */}
        {selected && (
          <DetailPanel
            customer={selected}
            onClose={() => setSelected(null)}
            onStatusChange={handleStatusChange}
            onInvoiceSent={handleInvoiceSent}
            onActivated={handleActivated}
            authFetch={authFetch}
          />
        )}
      </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const sty = {
  page: {
    minHeight: "100vh", background: "#060810",
    color: "#e2e8f0", fontFamily: "'DM Sans', system-ui, sans-serif",
  },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
    padding: "28px 40px 20px", borderBottom: "1px solid #111827",
  },
  breadcrumb: { fontSize: 11, color: "#334155", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 5, fontFamily: "'DM Mono', monospace" },
  title: { fontSize: 24, fontWeight: 800, color: "#f1f5f9", margin: "0 0 5px", letterSpacing: "-0.02em" },
  subtitle: { fontSize: 13, color: "#475569", margin: 0 },
  addBtn: {
    padding: "10px 18px", background: "linear-gradient(135deg, #1d4ed8, #2563eb)",
    border: "none", borderRadius: 8, color: "#fff",
    fontSize: 13, fontWeight: 700, textDecoration: "none",
    display: "inline-block", alignSelf: "flex-start",
    boxShadow: "0 4px 12px #1d4ed820",
  },
  kpiStrip: {
    display: "flex", background: "#090c14",
    borderBottom: "1px solid #111827",
  },
  kpi: {
    flex: 1, padding: "16px 24px",
    borderRight: "1px solid #111827",
    display: "flex", flexDirection: "column", gap: 3,
  },
  kpiVal: { fontSize: 22, fontWeight: 800, lineHeight: 1 },
  kpiLabel: { fontSize: 10, color: "#334155", letterSpacing: "0.07em", textTransform: "uppercase", fontFamily: "'DM Mono', monospace" },
  toolbar: {
    display: "flex", gap: 12, alignItems: "center",
    padding: "14px 40px", borderBottom: "1px solid #111827",
    background: "#06080d",
  },
  searchInput: {
    flex: 1, maxWidth: 320,
    background: "#0d1117", border: "1px solid #1f2937",
    borderRadius: 8, padding: "8px 12px",
    color: "#e2e8f0", fontSize: 13, outline: "none",
    fontFamily: "inherit",
  },
  filterGroup: { display: "flex", gap: 4 },
  filterBtn: {
    padding: "6px 14px", background: "none",
    border: "1px solid #1f2937", borderRadius: 20,
    color: "#475569", fontSize: 12, fontWeight: 600, cursor: "pointer",
  },
  filterBtnActive: { background: "#111827", color: "#f1f5f9", borderColor: "#334155" },
  mainLayout: { display: "grid", alignItems: "start" },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    fontSize: 10, fontWeight: 700, color: "#334155",
    letterSpacing: "0.08em", textTransform: "uppercase",
    padding: "10px 16px", textAlign: "left",
    background: "#07090e", borderBottom: "1px solid #111827",
    fontFamily: "'DM Mono', monospace",
    position: "sticky", top: 0,
  },
  tr: { borderBottom: "1px solid #0d1018", transition: "background 0.1s" },
  trSelected: { background: "#0d1525" },
  td: { padding: "14px 16px", verticalAlign: "middle" },
  custName: { fontSize: 13, fontWeight: 600, color: "#f1f5f9", marginBottom: 2 },
  custIndustry: { fontSize: 11, color: "#475569" },
  statusPill: {
    fontSize: 11, fontWeight: 700, borderRadius: 20,
    padding: "3px 9px", display: "inline-block",
    fontFamily: "'DM Mono', monospace",
  },
  fwTags: { display: "flex", gap: 4, flexWrap: "wrap" },
  fwTag: {
    fontSize: 10, fontWeight: 700, borderRadius: 4,
    padding: "2px 6px", fontFamily: "'DM Mono', monospace",
  },
  scoreVal: { fontSize: 15, fontWeight: 800, fontFamily: "'DM Mono', monospace" },
  loadingState: { padding: "48px", textAlign: "center", color: "#334155", fontSize: 13 },
  emptyState: { padding: "48px", textAlign: "center", color: "#334155", fontSize: 13 },
  // Detail panel
  detailPanel: {
    borderLeft: "1px solid #111827",
    background: "#07090e",
    minHeight: "calc(100vh - 200px)",
    display: "flex", flexDirection: "column", gap: 0,
    position: "sticky", top: 0,
  },
  detailHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
    padding: "20px", borderBottom: "1px solid #111827",
  },
  detailName: { fontSize: 15, fontWeight: 700, color: "#f1f5f9", marginBottom: 3 },
  detailIndustry: { fontSize: 12, color: "#475569" },
  closeBtn: {
    background: "none", border: "none",
    color: "#475569", fontSize: 14, cursor: "pointer", padding: 4,
  },
  detailSection: { padding: "16px 20px", borderBottom: "1px solid #0d1018" },
  detailSectionTitle: {
    fontSize: 9, fontWeight: 700, color: "#334155",
    letterSpacing: "0.1em", textTransform: "uppercase",
    marginBottom: 10, fontFamily: "'DM Mono', monospace",
  },
  detailRows: { display: "flex", flexDirection: "column", gap: 7 },
  detailRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 },
  detailRowLabel: { fontSize: 11, color: "#475569", flexShrink: 0 },
  detailRowValue: { fontSize: 12, color: "#94a3b8", textAlign: "right" },
  statusRow: { display: "flex", alignItems: "center", gap: 10 },
  statusActions: { display: "flex", gap: 6 },
  statusChangeBtn: {
    fontSize: 10, fontWeight: 700, background: "none",
    border: "1px solid", borderRadius: 5,
    padding: "3px 8px", cursor: "pointer",
    fontFamily: "'DM Mono', monospace",
  },
  detailActions: { padding: "16px 20px", marginTop: "auto" },
  actionBtn: {
    display: "block", textAlign: "center",
    padding: "10px", background: "#0d1525",
    border: "1px solid #1d4ed840", borderRadius: 8,
    color: "#93c5fd", fontSize: 13, fontWeight: 600,
    textDecoration: "none",
  },
  billingBtn: {
    display: "block", width: "100%", textAlign: "center",
    padding: "9px 12px", border: "1px solid",
    borderRadius: 7, fontSize: 12, fontWeight: 700,
    cursor: "pointer", fontFamily: "'DM Mono', monospace",
    letterSpacing: "0.03em",
  },
};

